/**
 * The country page's campaign-GROUP scope, pure: every group the operator
 * can switch to (the plan's own first, in execution order, then the rest
 * of the country by label), what each one holds (campaigns, containers,
 * its master config, pinned = every member pinned like the SMSWiser page),
 * and the plan's containers / day plan narrowed to one group.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenMasterSummary } from '@trafficpulse/shared';

import type { DayPlanPreview, DropGenContainerSummary, DropGenPlanDetail } from '../../drop-generator.types';
import type { GroupIndex } from './group-index';

export interface GroupEntry {
  key: string;
  label: string;
  fullLabel: string;
  members: Array<{ wiserId: number; name: string }>;
  containerCount: number;
  enabledCount: number;
  master: DropGenMasterSummary | null;
  pinned: boolean;
  /** Has a container or a master config in this plan. */
  inPlan: boolean;
}

export function groupEntries(
  plan: Pick<DropGenPlanDetail, 'containers' | 'masters'>,
  ix: GroupIndex,
  pinned: number[],
): GroupEntry[] {
  const order: string[] = [];
  const see = (key: string) => order.includes(key) || order.push(key);
  plan.containers.forEach((c) => see(ix.keyOf(c.campaignWiserId)));
  (plan.masters ?? []).forEach((m) => see(m.campaignGroupKey ?? ix.keyOf(m.campaignWiserId)));
  const rest = ix.groups.filter((g) => !order.includes(g.key)).sort((a, b) => a.label.localeCompare(b.label));
  const pinnedSet = new Set(pinned);
  return [...order, ...rest.map((g) => g.key)].map((key) => {
    const g = ix.group(key);
    const members = g ? g.campaigns.map(({ wiserId, name }) => ({ wiserId, name })) : orphanMembers(plan, ix, key);
    const containers = plan.containers.filter((c) => ix.keyOf(c.campaignWiserId) === key);
    return {
      key,
      label: g?.label ?? members.map((m) => m.name).join(', '),
      fullLabel: g?.fullLabel ?? members.map((m) => m.name).join(', '),
      members,
      containerCount: containers.length,
      enabledCount: containers.filter((c) => c.enabled).length,
      master: (plan.masters ?? []).find((m) => (m.campaignGroupKey ?? ix.keyOf(m.campaignWiserId)) === key) ?? null,
      pinned: members.length > 0 && members.every((m) => pinnedSet.has(m.wiserId)),
      inPlan: order.includes(key),
    };
  });
}

/** A campaign the plan uses but the catalog no longer lists: a group of one. */
function orphanMembers(plan: Pick<DropGenPlanDetail, 'containers'>, ix: GroupIndex, key: string) {
  const id = plan.containers.find((c) => ix.keyOf(c.campaignWiserId) === key)?.campaignWiserId;
  return id === undefined ? [] : [{ wiserId: id, name: ix.nameOf(id) }];
}

/** "5 campaigns · 3 containers · master config" — the one-line fact under a group. */
export function entryFacts(e: GroupEntry): string {
  const n = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
  const containers = e.containerCount === 0 ? 'no container yet' : n(e.containerCount, 'container');
  return `${n(e.members.length, 'campaign')} · ${containers} · ${e.master ? 'master config' : 'no master config'}`;
}

/** Long campaign names share prefixes — keep both ends so the part that
 *  tells them apart stays visible. */
export function middleTruncate(name: string, max: number): string {
  if (name.length <= max) return name;
  const head = Math.ceil((max - 1) * 0.45);
  return `${name.slice(0, head)}…${name.slice(name.length - (max - 1 - head))}`;
}

export function scopeContainers(
  containers: DropGenContainerSummary[],
  memberIds: ReadonlySet<number> | null,
): DropGenContainerSummary[] {
  return memberIds === null ? containers : containers.filter((c) => memberIds.has(c.campaignWiserId));
}

/** The day plan of one group: its containers, their totals, the warnings
 *  that name them (plus the ones that name no container). */
export function scopePreview(
  preview: DayPlanPreview | undefined,
  ids: ReadonlySet<string> | null,
): DayPlanPreview | undefined {
  if (!preview || ids === null) return preview;
  const containers = preview.containers.filter((c) => ids.has(c.containerId));
  const costs = containers.map((c) => c.totals.cost);
  return {
    ...preview,
    containers,
    totals: {
      drops: containers.reduce((s, c) => s + c.totals.drops, 0),
      sms: containers.reduce((s, c) => s + c.totals.sms, 0),
      cost: costs.some((c) => c === null) ? null : costs.reduce<number>((s, c) => s + (c ?? 0), 0),
    },
    warnings: preview.warnings.filter((w) => !w.containerId || ids.has(w.containerId)),
  };
}
